import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { caseApplicantService } from '@/api/caseApplicant';
import { GET_CLIENT_DIRECTORY } from '@/api';
import useModal from '@/hooks/useModal';
import { useNotification } from '@/hooks';
import { BulkRejectClients } from '@/interfaces';
import { CaseClientParsedFilters } from '@/hooks/useCaseClientFilters';

interface UseBulkRemoveFromCaseParams {
	selectedUserIds: (string | null)[];
	setSelectedUserIds: (usersIds: (string | null)[]) => void;
	caseId: string;
	parsedFilters: CaseClientParsedFilters;
	applicationIdByUserId: Record<string, number>;
	selectedCount: number;
	onSuccess?: () => void;
}

export const useBulkRemoveFromCase = ({
	selectedUserIds,
	setSelectedUserIds,
	caseId,
	parsedFilters,
	applicationIdByUserId,
	selectedCount,
	onSuccess,
}: UseBulkRemoveFromCaseParams) => {
	const { toastError, toastSuccess } = useNotification();
	const queryClient = useQueryClient();
	const {
		Modal: ModalBulkRemove,
		hide: hideBulkRemoveModal,
		show: showBulkRemoveModal,
	} = useModal();
	const [isRemoving, setIsRemoving] = useState(false);

	const isSingular = selectedCount === 1;

	const onConfirmBulkRemove = useCallback(async () => {
		if (isRemoving) return;

		const isSelectAll = selectedUserIds.includes(null);
		let application_ids: number[] = [];

		if (!isSelectAll) {
			const mappedIds: number[] = [];
			for (const userId of selectedUserIds) {
				if (!userId) continue;
				const applicationId = applicationIdByUserId[userId];
				if (applicationId == null) {
					toastError(
						isSingular
							? 'The client could not be removed from the case. Please try again.'
							: 'The clients could not be removed from the case. Please try again.'
					);
					return;
				}
				mappedIds.push(applicationId);
			}
			if (mappedIds.length === 0) {
				toastError(
					isSingular
						? 'The client could not be removed from the case. Please try again.'
						: 'The clients could not be removed from the case. Please try again.'
				);
				return;
			}
			application_ids = mappedIds;
		}

		try {
			setIsRemoving(true);
			const payload: BulkRejectClients = {
				caseId,
				application_ids,
				...parsedFilters,
			};
			await caseApplicantService.postBulkReject(payload);
			hideBulkRemoveModal();
			queryClient.invalidateQueries({ queryKey: [GET_CLIENT_DIRECTORY] });
			setSelectedUserIds([]);
			onSuccess?.();
			toastSuccess(
				isSingular
					? 'The client has been successfully removed from the case.'
					: 'The clients have been successfully removed from the case.'
			);
		} catch {
			toastError(
				isSingular
					? 'The client could not be removed from the case. Please try again.'
					: 'The clients could not be removed from the case. Please try again.'
			);
		} finally {
			setIsRemoving(false);
		}
	}, [
		isRemoving,
		selectedUserIds,
		applicationIdByUserId,
		caseId,
		parsedFilters,
		isSingular,
		hideBulkRemoveModal,
		queryClient,
		setSelectedUserIds,
		onSuccess,
		toastSuccess,
		toastError,
	]);

	return {
		ModalBulkRemove,
		showBulkRemoveModal,
		hideBulkRemoveModal,
		onConfirmBulkRemove,
		isRemoving,
		isSingular,
	};
};
